import { Navigate, Outlet, Route, Routes } from 'react-router';
import { AppShell } from './components/layout/AppShell';
import { useAuth } from './lib/auth';
import { LoginPage } from './routes/LoginPage';
import { InvitePage } from './routes/InvitePage';
import { ResetAccessPage } from './routes/ResetAccessPage';
import { OversigtPage } from './routes/OversigtPage';
import { KampagnerListPage } from './routes/KampagnerListPage';
import { KampagneEditPage } from './routes/KampagneEditPage';
import { GruppeSendPage } from './routes/GruppeSendPage';
import { KampagneSendPage } from './routes/KampagneSendPage';
import { StatistikPage } from './routes/StatistikPage';
import { SourceStatsPage } from './routes/SourceStatsPage';
import { DashboardPage } from './routes/DashboardPage';
import { BuyersPage } from './routes/BuyersPage';
import { KontakterListPage } from './routes/KontakterListPage';
import { KontaktPage } from './routes/KontaktPage';
import { AfmeldtePage } from './routes/AfmeldtePage';
import { BouncesPage } from './routes/BouncesPage';
import { AfmeldtPage } from './routes/AfmeldtPage';
import { AiSettingsPage } from './routes/AiSettingsPage';
import { BrandKitPage } from './routes/BrandKitPage';
import { SamtykketeksterPage } from './routes/SamtykketeksterPage';
import { AutomatiseringerPage } from './routes/AutomatiseringerPage';
import { KundemaerkaterPage } from './routes/KundemaerkaterPage';
import { AnmeldelserPage } from './routes/AnmeldelserPage';
import { SkabelonerListPage } from './routes/SkabelonerListPage';
import { SkabelonEditPage } from './routes/SkabelonEditPage';
import { AfsenderePage } from './routes/AfsenderePage';
import { ListerPage } from './routes/ListerPage';
import { SignupFormsPage } from './routes/SignupFormsPage';
import { FormsPage } from './routes/FormsPage';
import { LandingPagesPage } from './routes/LandingPagesPage';
import { SegmenterPage } from './routes/SegmenterPage';
import { SharedBlocksPage } from './routes/SharedBlocksPage';
import { CustomObjectsPage } from './routes/CustomObjectsPage';
import { WooPage } from './routes/WooPage';
import { BrugerePage } from './routes/BrugerePage';
import { ApiTokensPage } from './routes/ApiTokensPage';
import { SecurityPage } from './routes/SecurityPage';
import { AuditLogPage } from './routes/AuditLogPage';
import { CouponsPage } from './routes/CouponsPage';
import { KontakterImportPage } from './routes/KontakterImportPage';
import { MailchimpImportPage } from './routes/MailchimpImportPage';
import { OvergangFraMailchimpPage } from './routes/OvergangFraMailchimpPage';
import { SectionEditorPage } from './routes/SectionEditorPage';
import { TotpSetupPage } from './routes/TotpSetupPage';

/**
 * Same gating (loading, login, TOTP) for every signed-in page. `bare`
 * renders the page without the app sidebar: the campaign editor brings
 * its own compact navigation rail.
 */
function ProtectedShell({ bare = false }: { bare?: boolean }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted" role="status">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Every role must have TOTP before the API serves tenant data (security
  // fix 2026-09-25); `null` means no active tenant yet, not "missing".
  if (user.totpEnabled === false) return <TotpSetupPage />;
  return bare ? <Outlet /> : <AppShell />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite" element={<InvitePage />} />
      <Route path="/reset-access" element={<ResetAccessPage />} />
      {/* Legacy alias: old emailed reset links point here. Keep working. */}
      <Route path="/nulstil-adgang" element={<ResetAccessPage />} />
      <Route element={<ProtectedShell bare />}>
        <Route path="kampagner/:id" element={<KampagneEditPage />} />
        <Route path="kampagner/:id/sections" element={<SectionEditorPage kind="campaign" />} />
        <Route path="skabeloner/:id/sections" element={<SectionEditorPage kind="template" />} />
      </Route>
      <Route element={<ProtectedShell />}>
        <Route index element={<OversigtPage />} />
        <Route path="kampagner" element={<KampagnerListPage />} />
        <Route path="kampagner/:id/send" element={<KampagneSendPage />} />
        <Route path="kampagner/:id/send-all" element={<GruppeSendPage />} />
        <Route path="statistik" element={<StatistikPage />} />
        <Route path="statistik/dashboard" element={<DashboardPage />} />
        <Route path="statistik/tilmeldingskilder" element={<SourceStatsPage />} />
        <Route path="koebere" element={<BuyersPage />} />
        <Route path="kontakter" element={<KontakterListPage />} />
        <Route path="kontakter/import" element={<KontakterImportPage />} />
        <Route path="kontakter/:id" element={<KontaktPage />} />
        <Route path="kuponer" element={<CouponsPage />} />
        <Route path="afmeldte" element={<AfmeldtePage />} />
        <Route path="afmeldte/:id" element={<AfmeldtPage />} />
        <Route path="bounces" element={<BouncesPage />} />
        <Route path="automatiseringer" element={<AutomatiseringerPage />} />
        <Route path="automatiseringer/:id" element={<AutomatiseringerPage />} />
        <Route path="kundemaerkater" element={<KundemaerkaterPage />} />
        <Route path="anmeldelser" element={<AnmeldelserPage />} />
        <Route path="indstillinger/brand-kit" element={<BrandKitPage />} />
        <Route path="indstillinger/ai" element={<AiSettingsPage />} />
        <Route path="indstillinger/samtykketekster" element={<SamtykketeksterPage />} />
        <Route path="skabeloner" element={<SkabelonerListPage />} />
        <Route path="skabeloner/:id" element={<SkabelonEditPage />} />
        <Route path="indstillinger/afsendere" element={<AfsenderePage />} />
        <Route path="indstillinger/lister" element={<ListerPage />} />
        <Route path="indstillinger/signup-forms" element={<SignupFormsPage />} />
        <Route path="indstillinger/forms" element={<FormsPage />} />
        <Route path="indstillinger/landing-pages" element={<LandingPagesPage />} />
        <Route path="indstillinger/segmenter" element={<SegmenterPage />} />
        <Route path="indstillinger/delte-blokke" element={<SharedBlocksPage />} />
        <Route path="indstillinger/custom-objects" element={<CustomObjectsPage />} />
        <Route path="indstillinger/woo" element={<WooPage />} />
        <Route path="indstillinger/brugere" element={<BrugerePage />} />
        <Route path="indstillinger/api-tokens" element={<ApiTokensPage />} />
        <Route path="indstillinger/sikkerhed" element={<SecurityPage />} />
        <Route path="indstillinger/audit" element={<AuditLogPage />} />
        <Route path="indstillinger/mailchimp-import" element={<MailchimpImportPage />} />
        <Route path="indstillinger/overgang-fra-mailchimp" element={<OvergangFraMailchimpPage />} />
        <Route path="afsendere" element={<Navigate to="/indstillinger/afsendere" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
